import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

function formatErr(detail) {
    if (!detail) return "Something went wrong";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e.msg || JSON.stringify(e)).join(" ");
    if (detail?.msg) return detail.msg;
    return String(detail);
}

export default function Login() {
    const { login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            await login(email, password);
            toast.success("Welcome back");
            nav("/dashboard");
        } catch (e) {
            setErr(formatErr(e.response?.data?.detail) || e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[80vh] grid lg:grid-cols-2">
            <div className="hidden lg:block bg-black text-white p-16 flex flex-col justify-between relative overflow-hidden">
                <div>
                    <div className="text-overline text-[#E63946] mb-4">Welcome back</div>
                    <h1 className="font-display text-5xl font-black tracking-tighter leading-none uppercase">
                        Sign in to<br />your gallery.
                    </h1>
                </div>
                <div className="space-y-6">
                    <div className="border-l-2 border-[#E63946] pl-4">
                        <p className="text-lg leading-relaxed font-light">
                            "I keep my QR sticker on the dashboard of the van. Customers scan, see my work, sign the quote."
                        </p>
                        <div className="mt-3 text-xs uppercase tracking-widest text-neutral-400">Jamie · Roofer · Leeds</div>
                    </div>
                </div>
                <div className="absolute -bottom-20 -right-20 w-72 h-72 border-2 border-[#E63946] rotate-12 pointer-events-none" />
            </div>

            <div className="flex items-center justify-center p-8 sm:p-16">
                <form onSubmit={submit} className="w-full max-w-md fade-in-up" data-testid="login-form">
                    <h2 className="font-display text-3xl font-black tracking-tighter leading-none uppercase mb-2">
                        Sign in
                    </h2>
                    <p className="text-sm text-neutral-600 mb-8">
                        New here?{" "}
                        <Link to="/register" className="text-[#E63946] font-semibold hover:underline" data-testid="login-to-register">
                            Create a free gallery
                        </Link>
                    </p>

                    <label className="text-overline text-neutral-700 block mb-2">Email</label>
                    <input
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-field mb-5"
                        placeholder="you@buildersgallery.co.uk"
                        data-testid="login-email-input"
                    />

                    <label className="text-overline text-neutral-700 block mb-2">Password</label>
                    <input
                        type="password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="input-field mb-2"
                        placeholder="••••••••"
                        data-testid="login-password-input"
                    />

                    {err && (
                        <div className="mt-3 text-sm text-[#E63946]" data-testid="login-error">
                            {err}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="btn-primary w-full justify-center mt-6"
                        data-testid="login-submit-btn"
                    >
                        {loading ? "Signing in…" : "Sign in"} <ArrowRight size={16} weight="bold" />
                    </button>
                </form>
            </div>
        </div>
    );
}
